import { showCurrency } from '../../utils';
import { unitLabel } from '../../components/Attribution/attributionAccess';

export { unitLabel };

/**
 * Pure helpers for the business-units report (GET /api/attribution/report).
 * The API answers one `totals` object per entity and per unit (center),
 * keyed by currency; these flatten that into one table row per (unit,
 * currency) so the screen never adds two currencies together. Also the
 * partner settlement cards (`partners`) and the shared-costs split
 * (`sharedCosts`), always per currency.
 */

// Fixed display order; anything else the API ever returns goes after, alphabetically.
const CURRENCY_ORDER = ['UYU', 'USD'];

export const MEASURE_KEYS = ['facturado', 'cobrado', 'gastos', 'pagado', 'resultado'];

export function sortCurrencies(currencies) {
	return [...currencies].sort((a, b) => {
		const ia = CURRENCY_ORDER.indexOf(a);
		const ib = CURRENCY_ORDER.indexOf(b);
		if (ia !== -1 || ib !== -1) {
			return (ia === -1 ? CURRENCY_ORDER.length : ia) - (ib === -1 ? CURRENCY_ORDER.length : ib);
		}
		return a.localeCompare(b);
	});
}

function toNumber(value) {
	const n = Number(value);
	return Number.isFinite(n) ? n : 0;
}

function rowFromTotals(base, currency, totals) {
	const t = totals || {};
	return {
		...base,
		currency,
		facturado: toNumber(t.facturado),
		cobrado: toNumber(t.cobrado),
		gastos: toNumber(t.gastos),
		pagado: toNumber(t.pagado),
		resultado: toNumber(t.resultado),
		cuotas: toNumber(t.cuotas),
		gastosCount: toNumber(t.gastosCount),
		pasividades: toNumber(t.pasividades),
	};
}

/**
 * One row per (unit, currency), units in API order, then one total row
 * per currency flagged `isTotal`. Centers whose totals are empty produce no row.
 */
export function flattenEntityRows(entity) {
	if (!entity) return [];
	const rows = [];
	(entity.centers || []).forEach((center) => {
		const centerId = center.id || 'none';
		sortCurrencies(Object.keys(center.totals || {})).forEach((currency) => {
			rows.push(
				rowFromTotals(
					{ key: `${centerId}:${currency}`, centerId: center.id ?? null, centerName: center.name || 'General', isTotal: false },
					currency,
					center.totals[currency],
				),
			);
		});
	});
	sortCurrencies(Object.keys(entity.totals || {})).forEach((currency) => {
		rows.push(
			rowFromTotals(
				{ key: `total:${currency}`, centerId: null, centerName: 'Total entidad', isTotal: true },
				currency,
				entity.totals[currency],
			),
		);
	});
	return rows;
}

/** One stat per currency for the StatStrip: resultado headline, facturado/gastos as sub-text. */
export function entityStats(entity) {
	if (!entity) return [];
	return sortCurrencies(Object.keys(entity.totals || {})).map((currency) => {
		const row = rowFromTotals({}, currency, entity.totals[currency]);
		return {
			label: `Resultado ${currency}`,
			value: formatMoney(currency, row.resultado),
			mono: true,
			sub: `Facturado ${formatMoney(currency, row.facturado)} · Gastos ${formatMoney(currency, row.gastos)}`,
			subTone: row.resultado < 0 ? 'down' : 'neutral',
		};
	});
}

export function formatMoney(currency, amount) {
	const n = toNumber(amount);
	const symbol = showCurrency(currency) || currency || '';
	const formatted = Math.abs(n).toLocaleString('es-UY', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
	return `${n < 0 ? '-' : ''}${symbol} ${formatted}`.trim();
}

function pad(n) {
	return String(n).padStart(2, '0');
}

/** Default filter range: January 1st of the current year through today (local dates). */
export function defaultRange(today = new Date()) {
	const year = today.getFullYear();
	return {
		from: `${year}-01-01`,
		to: `${year}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`,
	};
}

/**
 * An empty `from` is allowed: it means "todo el historial" (the API then has
 * no lower bound). `to` is always required.
 */
export function rangeError({ from, to }) {
	if (!to) return 'Elegí la fecha "Hasta".';
	if (from && to < from) return 'La fecha "Hasta" debe ser posterior a "Desde".';
	return '';
}

// ---- display helpers ------------------------------------------------------

/** 'YYYY-MM-DD' → 'DD/MM/YYYY' (no timezone math: the API already sends calendar dates). */
export function formatDay(value) {
	if (!value) return '';
	const [y, m, d] = String(value).slice(0, 10).split('-');
	return d ? `${d}/${m}/${y}` : String(value);
}

/** 'YYYY-MM' → 'MM/YYYY'. */
export function formatMonth(value) {
	if (!value) return '';
	const [y, m] = String(value).split('-');
	return m ? `${m}/${y}` : String(value);
}

export function formatPercent(pct) {
	if (pct === null || pct === undefined || !Number.isFinite(pct)) return '—';
	return `${pct.toLocaleString('es-UY', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`;
}

/** Card heading for a partner unit: "Gotasoft · Ernesto — Ernesto Quesada". */
export function partnerHeading(entry) {
	if (!entry) return '';
	const unit = unitLabel(entry.unit, entry.entity);
	return entry.partner?.name ? `${unit} — ${entry.partner.name}` : unit;
}

// ---- partners (socios) ----------------------------------------------------

/** Settlement status of a partner row (month × client contract × currency). */
export const PARTNER_STATUS = {
	liquidado: { label: 'Liquidado', tone: 'ok' },
	'cobrado-sin-liquidar': { label: 'Cobrado, falta liquidar', tone: 'warn' },
	'cobrado-sin-liquidacion': { label: 'Cobrado, sin liquidación cargada', tone: 'danger' },
	'liquidado-sin-cobrar': { label: 'Liquidado antes de cobrar', tone: 'accent' },
	'pendiente-de-cobro': { label: 'Pendiente de cobro', tone: 'neutral' },
};

export const PARTNER_STATUS_ORDER = Object.keys(PARTNER_STATUS);

export function partnerStatus(estado) {
	return PARTNER_STATUS[estado] || { label: estado || '—', tone: 'neutral' };
}

/** Rows with a stable `key` and their status label/tone, optionally narrowed to one `estado` ('' = todos). */
export function partnerRows(entry, estado = '') {
	return (entry?.rows || [])
		.map((row, index) => ({
			...row,
			key: `${row.month}:${row.contractId || 'none'}:${row.currency}:${index}`,
			status: partnerStatus(row.estado),
		}))
		.filter((row) => !estado || row.estado === estado);
}

/** { estado: count } over every row of the partner, for the filter options. */
export function partnerStatusCounts(entry) {
	const counts = {};
	(entry?.rows || []).forEach((row) => {
		counts[row.estado] = (counts[row.estado] || 0) + 1;
	});
	return counts;
}

/** Options for the Estado filter: "Todos" first, then every status with its count. */
export function partnerStatusOptions(entry) {
	const counts = partnerStatusCounts(entry);
	const total = (entry?.rows || []).length;
	return [
		{ value: '', label: `Todos (${total})` },
		...PARTNER_STATUS_ORDER.map((estado) => ({ value: estado, label: `${PARTNER_STATUS[estado].label} (${counts[estado] || 0})` })),
	];
}

/**
 * One StatStrip per currency for a partner unit: what was invoiced to their
 * clients, collected and paid out in the period, plus where the settlement
 * stands today (`balance`, everything up to `to`): collected but not yet paid
 * out to the partner (warning when > 0) and paid out before the client paid.
 */
export function partnerStats(entry) {
	if (!entry) return [];
	const summary = entry.summary || {};
	const balance = entry.balance || {};
	const currencies = sortCurrencies([...new Set([...Object.keys(summary), ...Object.keys(balance)])]);
	return currencies.map((currency) => {
		const s = summary[currency] || {};
		const b = balance[currency] || {};
		const aLiquidar = toNumber(s.aLiquidar);
		const porLiquidar = toNumber(b.porLiquidar);
		const adelantado = toNumber(b.adelantado);
		return {
			currency,
			porLiquidar,
			stats: [
				{ label: 'Facturado a sus clientes', value: formatMoney(currency, s.facturado), mono: true },
				{ label: 'Cobrado', value: formatMoney(currency, s.cobrado), mono: true },
				{
					label: 'Liquidado al socio',
					value: formatMoney(currency, s.liquidado),
					mono: true,
					sub: aLiquidar ? `De ${formatMoney(currency, aLiquidar)} a liquidar` : undefined,
				},
				{
					label: 'Cobrado sin liquidar',
					value: formatMoney(currency, porLiquidar),
					mono: true,
					sub: 'a hoy',
					accent: porLiquidar > 0 ? '--color-warn' : undefined,
				},
				{ label: 'Liquidado antes de cobrar', value: formatMoney(currency, adelantado), mono: true, sub: 'a hoy' },
			],
		};
	});
}

/**
 * The second, smaller strip of a partner card: the company's shared costs
 * charged to the partner (bill payer shares) and the money held in their
 * favor. Labels carry the currency so tiles of different currencies never merge.
 */
export function partnerSideStats(entry) {
	if (!entry) return [];
	const costs = entry.costs || {};
	const credit = entry.credit || {};
	const costsTitle = `Costos de ${entry.entity?.name || 'la empresa'} a su cargo`;
	const stats = [];
	sortCurrencies(Object.keys(costs)).forEach((currency) => {
		const c = costs[currency] || {};
		const pendiente = toNumber(c.pendiente);
		stats.push({
			label: `${costsTitle} · ${currency}`,
			value: formatMoney(currency, c.asignado),
			mono: true,
			small: true,
			sub: `Pagado ${formatMoney(currency, c.pagado)} · Pendiente ${formatMoney(currency, pendiente)}`,
			subTone: pendiente > 0 ? 'down' : 'neutral',
		});
	});
	sortCurrencies(Object.keys(credit)).forEach((currency) => {
		const aFavor = toNumber(credit[currency]?.aFavor);
		if (!aFavor) return;
		stats.push({
			label: `Saldo a favor · ${currency}`,
			value: formatMoney(currency, aFavor),
			mono: true,
			small: true,
			sub: 'Cobros sin imputar a sus deudas',
		});
	});
	return stats;
}

// ---- shared costs ----------------------------------------------------------

function percentOf(amount, total) {
	if (!total) return null;
	return Math.round((amount / total) * 1000) / 10;
}

/**
 * Per currency of one entity's shared costs: the total, one line per partner
 * (largest first) and the company's own part, each with its % of the total.
 * → [{ currency, total, lines: [{ key, label, amount, pct, kind: 'socio' | 'empresa' }] }]
 */
export function sharedCostLines(entry) {
	if (!entry) return [];
	const totals = entry.totals || {};
	return sortCurrencies(Object.keys(totals)).map((currency) => {
		const t = totals[currency] || {};
		const total = toNumber(t.total);
		const socios = Object.entries(t.socios || {})
			.map(([name, amount]) => ({ name, amount: toNumber(amount) }))
			.sort((a, b) => b.amount - a.amount || a.name.localeCompare(b.name));
		const empresa = toNumber(t.empresa);
		return {
			currency,
			total,
			lines: [
				...socios.map((s) => ({ key: `socio:${s.name}`, label: s.name, amount: s.amount, pct: percentOf(s.amount, total), kind: 'socio' })),
				{ key: 'empresa', label: 'La empresa', amount: empresa, pct: percentOf(empresa, total), kind: 'empresa' },
			],
		};
	});
}

/**
 * Table rows for one entity's shared costs: per currency a "Total" row
 * (`isTotal`) followed by its partner lines and "La empresa".
 */
export function sharedCostRows(entry) {
	const rows = [];
	sharedCostLines(entry).forEach(({ currency, total, lines }) => {
		rows.push({ key: `${currency}:total`, currency, label: 'Total', amount: total, pct: total ? 100 : null, kind: 'total', isTotal: true });
		lines.forEach((line) => rows.push({ ...line, key: `${currency}:${line.key}`, currency, isTotal: false }));
	});
	return rows;
}
